/**
 * The no-show rule — what actually happens to the fare when a driver marks a no-show.
 *
 * The lateness popup used to state this as hardcoded copy ("Full" / "fare" / "Full fare
 * may apply."). Only the THRESHOLD was ever real: the minutes come from
 * /trips/calculate-policies, but the consequence was our own assumption, so an admin who
 * configured anything other than "keep 100%" would have been contradicted on screen.
 *
 * The rule is admin-configured PER DIRECTION and PER DISTANCE BAND, and arrives on
 * `/config/policies` (§3 of api-changes-2026-08) as:
 *
 *   cancellation.directions[].distance_bands[].no_show = {
 *     refund_percentage, credit_percentage, kept_percentage, flat_fee_cents, text
 *   }
 *
 * A trip's own policy payload may also carry a `no_show` block already resolved for that
 * trip — that is always preferred when present, since it needs no matching at all.
 */

/**
 * Pick the no-show block that applies, given whatever trip context the caller has.
 *
 * Matching is deliberately conservative, because a WRONG fare rule on screen is worse
 * than the honest generic sentence it replaces:
 *
 *  - direction known  → that direction's bands (falls back to nothing if it isn't listed)
 *  - distance known   → the band whose [from_km, to_km) contains it
 *  - neither known    → the candidates are used ONLY if they all say the same thing;
 *                       one band disagreeing means we cannot answer and return null
 *
 * A null return is the caller's signal to keep its static copy.
 *
 * @param {object|null} catalog `/config/policies` response
 * @param {{trip_direction?: string, distance_meters?: number}} [params]
 * @returns {{refundPercentage: number, creditPercentage: number, keptPercentage: number, flatFeeCents: number, text: string}|null}
 */
export function resolveNoShow(catalog, {trip_direction: direction, distance_meters: distanceMeters} = {}) {
    const directions = catalog?.cancellation?.directions;
    if (!Array.isArray(directions) || directions.length === 0) return null;

    const scoped = direction
        ? directions.filter((d) => d?.direction === direction)
        : directions;
    if (scoped.length === 0) return null;

    // Every band we might be looking at, flattened — one direction's worth when the
    // direction is known, the whole catalog's when it isn't.
    const bands = scoped.flatMap((d) => (Array.isArray(d?.distance_bands) ? d.distance_bands : []));
    const withRule = bands.filter((b) => b?.no_show);
    if (withRule.length === 0) return null;

    // A distance picks exactly one band. `to_km: null` is the open-ended top band.
    if (direction && Number.isFinite(distanceMeters) && distanceMeters > 0) {
        const km = distanceMeters / 1000;
        const hit = withRule.find((b) => {
            const from = Number(b.from_km) || 0;
            const to = b.to_km == null ? Infinity : Number(b.to_km);
            return km >= from && km < to;
        });
        if (hit) return normalizeNoShow(hit.no_show);
    }

    // No distance (or nothing matched it): answer only if the candidates agree.
    const first = normalizeNoShow(withRule[0].no_show);
    const allAgree = withRule.every((b) => sameRule(normalizeNoShow(b.no_show), first));
    return allAgree ? first : null;
}

/** Read a `no_show` block straight off a trip's / booking's own resolved policy. */
export function noShowFromPolicy(policyBlock) {
    return policyBlock?.no_show ? normalizeNoShow(policyBlock.no_show) : null;
}

function normalizeNoShow(raw) {
    return {
        refundPercentage: Number(raw?.refund_percentage) || 0,
        creditPercentage: Number(raw?.credit_percentage) || 0,
        keptPercentage: Number(raw?.kept_percentage) || 0,
        flatFeeCents: Number(raw?.flat_fee_cents) || 0,
        text: raw?.text || '',
    };
}

const sameRule = (a, b) =>
    a.keptPercentage === b.keptPercentage
    && a.refundPercentage === b.refundPercentage
    && a.flatFeeCents === b.flatFeeCents;

/**
 * The rule as the popup's big-value tier row wants it: `{value, unit, subtitle}`.
 *
 * "Full fare" is only correct at 100% kept — every other configuration renders the
 * number the admin actually set. A flat fee wins over a percentage when one is set,
 * since that is then the whole charge.
 *
 * Returns null when there is no rule to show, so callers keep their fallback row.
 */
export function noShowTierRow(noShow) {
    if (!noShow) return null;

    if (noShow.flatFeeCents > 0) {
        const dollars = noShow.flatFeeCents / 100;
        return {
            value: `$${dollars % 1 === 0 ? dollars : dollars.toFixed(2)}`,
            unit: 'fee',
            subtitle: noShow.text,
        };
    }

    const kept = noShow.keptPercentage;
    return {
        value: kept >= 100 ? 'Full' : `${kept}%`,
        unit: 'fare',
        subtitle: noShow.text,
    };
}
